import { useMemo } from "react";
import { StyleProp, ViewStyle, useWindowDimensions } from "react-native";

type ResponsiveColumnsConfig = {
  breakpoint: number;
  maxItemWidth: number;
  gap: number;
  columns?: number;
};

type ResponsiveColumnsResult = {
  containerStyle: StyleProp<ViewStyle>;
  getItemStyle: (index: number) => StyleProp<ViewStyle>;
  columns: number;
  isMultiColumn: boolean;
};

export const useResponsiveColumns = (
  config: ResponsiveColumnsConfig,
): ResponsiveColumnsResult => {
  const { width } = useWindowDimensions();
  const { breakpoint, maxItemWidth, gap, columns } = config;
  const isMultiColumn = width >= breakpoint;

  // Calculate optimal number of columns based on available width and maxItemWidth
  const activeColumns = useMemo(() => {
    if (!isMultiColumn) return 1;

    // If columns is explicitly set, use it
    if (columns !== undefined) return columns;

    // Otherwise, dynamically calculate based on width
    // Calculate how many items of maxItemWidth can fit with gaps
    const availableWidth = width;
    const columnsToFit = Math.floor(
      (availableWidth + gap) / (maxItemWidth + gap),
    );
    return Math.max(1, columnsToFit);
  }, [isMultiColumn, width, maxItemWidth, gap, columns]);

  const containerStyle = useMemo<StyleProp<ViewStyle>>(() => {
    if (!isMultiColumn) return {};
    return {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "flex-start",
    };
  }, [isMultiColumn]);

  const getItemStyle = useMemo(() => {
    if (!isMultiColumn) {
      return (_index: number): StyleProp<ViewStyle> => ({
        width: "100%",
        marginBottom: gap,
      });
    }

    return (index: number): StyleProp<ViewStyle> => {
      const isEndOfRow = (index + 1) % activeColumns === 0;
      const itemWidth =
        `calc((100% - ${gap * (activeColumns - 1)}px) / ${activeColumns})` as any;
      return {
        width: itemWidth,
        maxWidth: maxItemWidth,
        marginRight: isEndOfRow ? 0 : gap,
        marginBottom: gap,
      };
    };
  }, [isMultiColumn, activeColumns, maxItemWidth, gap]);

  return {
    containerStyle,
    getItemStyle,
    columns: activeColumns,
    isMultiColumn,
  };
};
